import React from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Typography,
  Paper,
  Fade,
  Grid
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import './LoadingStates.css';

// Main loading spinner
interface LoadingSpinnerProps {
  size?: number;
  message?: string;
  fullScreen?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 40,
  message = 'Loading...',
  fullScreen = false
}) => {
  const content = (
    <Box className="loading-spinner" sx={{ textAlign: 'center', p: 3, bgcolor: 'transparent' }}>
      <CircularProgress size={size} sx={{ color: 'white' }} />
      {message && (
        <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.9)' }}>
          {message}
        </Typography>
      )}
    </Box>
  );

  if (fullScreen) {
    return (
      <Box className="loading-fullscreen">
        {content}
      </Box>
    );
  }

  return content;
};

// Progress bar with message
interface ProgressBarProps {
  progress: number;
  message?: string;
  showPercentage?: boolean;
  variant?: 'determinate' | 'indeterminate';
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  message,
  showPercentage = true,
  variant = 'determinate'
}) => {
  return (
    <Box className="progress-bar" sx={{ width: '100%', my: 2 }}>
      {message && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {message}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ width: '100%', mr: 1 }}>
          <LinearProgress
            variant={variant}
            value={variant === 'determinate' ? progress : undefined}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
        {showPercentage && variant === 'determinate' && (
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">
              {`${Math.round(progress)}%`}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Skeleton loader for profiles
export const ProfileSkeleton: React.FC = () => {
  return (
    <Paper className="profile-skeleton" sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Skeleton variant="circular" width={60} height={60} sx={{ mr: 2 }} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" sx={{ fontSize: '1.5rem', width: '60%' }} />
          <Skeleton variant="text" sx={{ fontSize: '1rem', width: '40%' }} />
        </Box>
      </Box>
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
      <Skeleton variant="text" sx={{ fontSize: '1rem', width: '80%' }} />
    </Paper>
  );
};

// Skeleton loader for user list
export const UserListSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <Box className="user-list-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" sx={{ width: '30%' }} />
            <Skeleton variant="text" sx={{ width: '20%', fontSize: '0.875rem' }} />
          </Box>
          <Skeleton variant="rectangular" width={80} height={32} />
        </Box>
      ))}
    </Box>
  );
};

// Generation steps progress
interface GenerationStep {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
}

interface GenerationProgressProps {
  steps: GenerationStep[];
  currentStep?: string;
}

export const GenerationProgress: React.FC<GenerationProgressProps> = ({ steps, currentStep }) => {
  console.log('GenerationProgress render - steps:', steps, 'currentStep:', currentStep);
  
  return (
    <Box className="generation-progress" sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Generating Bios
      </Typography>
      <Box sx={{ mt: 2 }}>
        {steps.map((step, index) => (
          <Fade in key={step.id} timeout={300 * (index + 1)}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {step.status === 'completed' ? (
                <CheckCircle color="success" sx={{ mr: 2 }} />
              ) : step.status === 'active' ? (
                <CircularProgress size={24} sx={{ mr: 2 }} />
              ) : (
                <RadioButtonUnchecked color="disabled" sx={{ mr: 2 }} />
              )}
              <Typography
                variant="body1"
                color={step.status === 'pending' ? 'text.disabled' : 'text.primary'}
                sx={{ fontWeight: step.status === 'active' ? 'bold' : 'normal' }}
              >
                {step.label}
              </Typography>
            </Box>
          </Fade>
        ))}
      </Box>
    </Box>
  );
};

// Content loader with timeout
interface ContentLoaderProps {
  loading: boolean;
  error?: Error | null;
  timeout?: number;
  onTimeout?: () => void;
  children: React.ReactNode;
  skeleton?: React.ReactNode;
}

export const ContentLoader: React.FC<ContentLoaderProps> = ({
  loading,
  error,
  timeout = 30000,
  onTimeout,
  children,
  skeleton
}) => {
  const [timedOut, setTimedOut] = React.useState(false);

  React.useEffect(() => {
    if (loading && timeout) {
      const timer = setTimeout(() => {
        setTimedOut(true);
        if (onTimeout) onTimeout();
      }, timeout);

      return () => clearTimeout(timer);
    }
  }, [loading, timeout, onTimeout]);

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="error">Failed to load content</Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message}
        </Typography>
      </Box>
    );
  }

  if (timedOut) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="warning.main">Loading is taking longer than expected</Typography>
        <Typography variant="body2" color="text.secondary">
          Please check your connection and try again
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return <>{skeleton || <LoadingSpinner />}</>;
  }

  return <>{children}</>;
};

// Pulsing placeholder
interface PulsePlaceholderProps {
  height?: number | string;
  width?: number | string;
  borderRadius?: number;
}

export const PulsePlaceholder: React.FC<PulsePlaceholderProps> = ({
  height = 20,
  width = '100%',
  borderRadius = 4
}) => {
  return (
    <Box
      className="pulse-placeholder"
      sx={{
        height,
        width,
        borderRadius: `${borderRadius}px`,
        bgcolor: 'grey.300',
        animation: 'pulse 1.5s ease-in-out infinite'
      }}
    />
  );
};

// Card skeleton
export const CardSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
      <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
      <Skeleton variant="text" />
      <Skeleton variant="text" />
      <Skeleton variant="text" width="60%" />
    </Paper>
  );
};

// Table skeleton
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 4 }) => {
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', p: 2, borderBottom: '2px solid #e0e0e0' }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Box key={index} sx={{ flex: 1, px: 1 }}>
            <Skeleton variant="text" sx={{ fontSize: '1rem', fontWeight: 'bold' }} />
          </Box>
        ))}
      </Box>
      
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box key={rowIndex} sx={{ display: 'flex', p: 2, borderBottom: '1px solid #e0e0e0' }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Box key={colIndex} sx={{ flex: 1, px: 1 }}>
              <Skeleton variant="text" />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
};

// Form skeleton
export const FormSkeleton: React.FC = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Skeleton variant="text" sx={{ mb: 1 }} width="30%" />
          <Skeleton variant="rectangular" height={56} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Skeleton variant="text" sx={{ mb: 1 }} width="30%" />
          <Skeleton variant="rectangular" height={56} />
        </Grid>
        <Grid size={12}>
          <Skeleton variant="text" sx={{ mb: 1 }} width="20%" />
          <Skeleton variant="rectangular" height={120} />
        </Grid>
        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Skeleton variant="rectangular" width={100} height={40} />
            <Skeleton variant="rectangular" width={100} height={40} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// Export panel skeleton
export const ExportPanelSkeleton: React.FC = () => {
  return (
    <Paper sx={{ p: 3 }}>
      <Skeleton variant="text" sx={{ fontSize: '1.5rem', mb: 2 }} width="40%" />
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Skeleton variant="rectangular" width={120} height={60} />
        <Skeleton variant="rectangular" width={120} height={60} />
      </Box>
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="text" width="40%" />
    </Paper>
  );
};

// Animated dots loader
export const DotsLoader: React.FC<{ message?: string }> = ({ message = 'Loading' }) => {
  return (
    <Box className="dots-loader" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <span className="dot" style={{ animationDelay: '0s' }}>.</span>
        <span className="dot" style={{ animationDelay: '0.2s' }}>.</span>
        <span className="dot" style={{ animationDelay: '0.4s' }}>.</span>
      </Box>
    </Box>
  );
};

// Export all loading components
const LoadingComponents = {
  LoadingSpinner,
  ProgressBar,
  ProfileSkeleton,
  UserListSkeleton,
  GenerationProgress,
  ContentLoader,
  PulsePlaceholder,
  CardSkeleton,
  TableSkeleton,
  FormSkeleton,
  ExportPanelSkeleton,
  DotsLoader
};

export default LoadingComponents;